import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import { useQuery } from 'react-query';
import { DigitalStylingTableHeader } from '../../table/view/DigitalStylingTableHeader';
import { DigitalStylingTableBody } from '../../table/view/DigitalStylingTableBody';
import { ListFilters } from './ListFilters';
import { DigitalStylingOrderListItem } from '../../shared/types';
import { DigitalStylingOrderListApi } from '../queries/DigitalStylingOrderListApi';
import { Translate } from '../../../../../../shared/LanguageService';

type DigitalStylingOrderListPageLabels = {
    title: string;
    orderStatus: (displayName: string) => string;
    header: {
        orderNumber: string;
        broker: string;
        product: string;
        address: string;
        orderStatus: string;
        createdAt: string;
        photoTime: string;
        assignedUser: string;
    };
    filter: {
        orderStatus: string;
        optionAll: string;
    };
};

type DigitalStylingOrderListPageProps = {
    labels: DigitalStylingOrderListPageLabels;
    orders: DigitalStylingOrderListItem[];
    availableOrderStatuses: { id: number; name: string }[];
};

const REFETCH_INTERVAL = 10 * 60 * 1000;

function byOrderStatus(selectedOrderStatus: number) {
    return function func(order: DigitalStylingOrderListItem): boolean {
        if (selectedOrderStatus === 0) {
            return true;
        }

        return order.orderStatus.value === selectedOrderStatus;
    };
}

function DigitalStylingOrderListPage({
    labels,
    orders,
    availableOrderStatuses
}: DigitalStylingOrderListPageProps) {
    let [selectedOrderStatus, setSelectedOrderStatus] = React.useState(0);

    function handleChangeOrderStatus(event: React.ChangeEvent<HTMLInputElement>) {
        let selectedOrderStatus = Number(event.target.value);
        setSelectedOrderStatus(selectedOrderStatus);
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Typography sx={{ fontSize: '32px' }} variant="h1">
                {labels.title}
            </Typography>
            <ListFilters
                availableOrderStatuses={availableOrderStatuses}
                onChangeOrderStatus={handleChangeOrderStatus}
                selectedOrderStatus={selectedOrderStatus}
                labels={{
                    orderStatus: labels.orderStatus,
                    filter: {
                        orderStatus: labels.filter.orderStatus,
                        optionAll: labels.filter.optionAll
                    }
                }}
            />
            <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                    <DigitalStylingTableHeader
                        labels={{
                            orderNumber: labels.header.orderNumber,
                            broker: labels.header.broker,
                            product: labels.header.product,
                            address: labels.header.address,
                            orderStatus: labels.header.orderStatus,
                            createdAt: labels.header.createdAt,
                            photoTime: labels.header.photoTime,
                            assignedUser: labels.header.assignedUser
                        }}
                    />
                    <DigitalStylingTableBody
                        orders={orders.filter(byOrderStatus(selectedOrderStatus))}
                        labels={{
                            orderStatus: labels.orderStatus
                        }}
                    />
                </Table>
            </TableContainer>
        </Box>
    );
}


export const DigitalStylingOrderListQueryKey = 'digital-styling-orders';

export default function DigitalStylingOrderListPageDataLayer() {
    let labels: DigitalStylingOrderListPageLabels = {
        title: Translate('orders-list/digital-styling/title'),
        orderStatus: (displayName: string) => Translate(displayName),
        header: {
            orderNumber: Translate('orders-list/digital-styling/header/orderNumber'),
            broker: Translate('orders-list/digital-styling/header/broker'),
            product: Translate('orders-list/digital-styling/header/product'),
            address: Translate('orders-list/digital-styling/header/address'),
            orderStatus: Translate('orders-list/digital-styling/header/orderStatus'),
            createdAt: Translate('orders-list/digital-styling/header/createdAt'),
            photoTime: Translate('orders-list/digital-styling/header/photoTime'),
            assignedUser: Translate('orders-list/digital-styling/header/assignedUser')
        },
        filter: {
            orderStatus: Translate('orders-list/digital-styling/filter/orderStatus'),
            optionAll: Translate('orders-list/digital-styling/filter/option/all')
        }
    };

    let { data, isError, error } = useQuery(
        [DigitalStylingOrderListQueryKey],
        () => DigitalStylingOrderListApi.getDigitalStylingOrders(),
        {
            refetchInterval: REFETCH_INTERVAL
        }
    );

    if (isError) {
        console.error('Error fetching digital styling orders', error);
    }

    let { orders, availableOrderStatuses } = data
        ? data
        : { orders: [], availableOrderStatuses: [] };

    return (
        <DigitalStylingOrderListPage
            labels={labels}
            orders={orders}
            availableOrderStatuses={availableOrderStatuses}
        />
    );
}
